/**
 * Generate a thumbnail from an image blob
 * @param blob - Original image blob
 * @param maxWidth - Maximum thumbnail width
 * @param maxHeight - Maximum thumbnail height
 * @returns Thumbnail blob
 */
export async function createImageThumbnail(
  blob: Blob,
  maxWidth: number = 300,
  maxHeight: number = 300,
): Promise<Blob> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    const url = URL.createObjectURL(blob);

    img.onload = () => {
      // Calculate thumbnail dimensions while maintaining aspect ratio
      let width = img.width;
      let height = img.height;

      if (width > height) {
        if (width > maxWidth) {
          height = (height * maxWidth) / width;
          width = maxWidth;
        }
      } else {
        if (height > maxHeight) {
          width = (width * maxHeight) / height;
          height = maxHeight;
        }
      }

      // Create canvas and draw resized image
      const canvas = document.createElement('canvas');
      canvas.width = width;
      canvas.height = height;
      const ctx = canvas.getContext('2d');

      if (!ctx) {
        URL.revokeObjectURL(url);
        reject(new Error('Could not get canvas context'));
        return;
      }

      ctx.drawImage(img, 0, 0, width, height);

      // Convert canvas to blob
      canvas.toBlob(
        (thumbnailBlob) => {
          URL.revokeObjectURL(url);
          if (thumbnailBlob) {
            resolve(thumbnailBlob);
          } else {
            reject(new Error('Failed to create thumbnail blob'));
          }
        },
        blob.type,
        // Only apply quality for JPEG/WebP, PNG doesn't support it
        blob.type === 'image/jpeg' || blob.type === 'image/webp' ? 0.8 : undefined,
      );
    };

    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error('Failed to load image'));
    };

    img.src = url;
  });
}

/**
 * Check if a file is a GIF
 */
export function isGif(mimeType: string): boolean {
  return mimeType === 'image/gif';
}

/**
 * Check if a file type should be cached as thumbnail only
 */
export function shouldCacheThumbnailOnly(mimeType: string): boolean {
  // Cache full GIFs (they're animations)
  if (isGif(mimeType)) {
    return false;
  }

  // For other images, cache thumbnails only
  if (mimeType.startsWith('image/')) {
    return true;
  }

  // For videos, don't auto-cache (load on demand)
  if (mimeType.startsWith('video/')) {
    return false;
  }

  return false;
}
